import React, { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { shortDate } from "@/lib/format";
import { BellRinging, ArrowDownLeft, ArrowUpRight, Warning } from "@phosphor-icons/react";

export default function Notifications() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 15000,
  });

  useEffect(() => {
    if (data?.unread > 0) {
      api.post("/notifications/read").then(() => qc.invalidateQueries({ queryKey: ["notifications"] })).catch(() => {});
    }
  }, [data?.unread, qc]);

  const items = data?.notifications || [];

  const iconFor = (n) => {
    if (n.type === "low_balance") return <Warning size={18} weight="fill" className="text-amber-600" />;
    if (n.direction === "credit") return <ArrowDownLeft size={18} weight="bold" className="text-emerald-600" />;
    if (n.direction === "debit") return <ArrowUpRight size={18} weight="bold" className="text-primary" />;
    return <BellRinging size={18} weight="fill" className="text-primary" />;
  };

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Notifications</h1>
        <p className="mt-2 text-muted-foreground">Wallet activity, low-balance alerts and account updates.</p>
      </div>
      <div className="overflow-hidden rounded-2xl border border-border bg-card">
        {items.length === 0 && <p className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="notifications-empty">You're all caught up</p>}
        {items.map((n) => (
          <div key={n.id} data-testid={`notification-${n.id}`}
            className={`flex items-start gap-3 border-b border-border px-4 py-3 last:border-0 ${n.read ? "" : "bg-accent/40"}`}>
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background">{iconFor(n)}</div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{n.title}</p>
              <p className="text-sm text-muted-foreground">{n.body}</p>
              <p className="mt-1 text-xs text-muted-foreground">{shortDate(n.created_at)}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
